import { Component, Input, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Attraction, FLAG_FREE_CANCELLATION, FLAG_LIKELY_TO_SELL_OUT, PICKUP_MEET_AT_START } from '../../models/attraction.model';
import { AttractionsService } from '../../services/attractions.service';
import { AttractionDetailsSheetService } from '../../services/attraction-details-sheet.service';
import { formatAttractionPrice } from '../attraction-utils';
import { AnalyticsService } from '../../services/analytics.service';

/**
 * Full content for one attraction (Android `AttractionDetailsScreen`), presented as the global
 * overlay bottom sheet hosted in AppComponent. Follows the GetYourGuide details layout: inset hero
 * carousel, title + aggregate rating, icon-tile fact rows, ratings breakdown, and a sticky price +
 * CTA bar. Every section renders ONLY when its API field is present — nothing is invented.
 */
@Component({
    selector: 'app-attraction-details',
    templateUrl: './attraction-details.component.html',
    styleUrls: ['./attraction-details.component.scss'],
    host: { class: 'attractions-scope' },
    standalone: false
})
export class AttractionDetailsComponent implements OnDestroy {
  readonly FLAG_FREE_CANCELLATION = FLAG_FREE_CANCELLATION;
  readonly FLAG_LIKELY_TO_SELL_OUT = FLAG_LIKELY_TO_SELL_OUT;

  detail: Attraction | null = null;
  loading = false;
  error: string | null = null;

  // Derived, computed once on load so the template stays declarative.
  galleryImages: string[] = [];
  price: string | null = null;
  beforePrice: string | null = null;
  durationText: string | null = null;
  languagesText: string | null = null;
  pickupText: string | null = null;
  goodToKnowText: string | null = null;
  inclusions: string[] = [];
  exclusions: string[] = [];
  cancellationText: string | null = null;
  meetingPointText: string | null = null;
  showRatingsBreakdown = false;

  private _code: string | null = null;

  constructor(
    private attractionsService: AttractionsService,
    private sheet: AttractionDetailsSheetService,
    private translate: TranslateService,
    private analytics: AnalyticsService
  ) {}

  @Input() set code(value: string | null) {
    this._code = value;
    if (value) {
      this.loadDetails(value);
    }
  }
  get code(): string | null {
    return this._code;
  }

  ngOnDestroy(): void {
    // Safety net: if the component is torn down while the class is still set (e.g. a route change
    // races the *ngIf), make sure the scroll-lock never lingers.
    document.body.classList.remove('attr-sheet-open');
  }

  private shopId(): number | string | null {
    try {
      const raw = sessionStorage.getItem('clientData');
      if (!raw) return null;
      const clientData = JSON.parse(raw);
      return clientData?.id ?? null;
    } catch {
      return null;
    }
  }

  private async loadDetails(code: string): Promise<void> {
    const shopId = this.shopId();
    if (shopId === null) {
      this.loading = false;
      this.error = this.translate.instant('attractions.error');
      return;
    }

    this.loading = true;
    this.error = null;
    this.detail = null;

    const product = await this.attractionsService.getAttraction(shopId, code);
    this.loading = false;

    if (!product) {
      this.error = this.translate.instant('attractions.error');
      return;
    }

    this.detail = product;
    this.recompute(product);
  }

  private recompute(product: Attraction): void {
    this.galleryImages = (product.image_urls ?? []).filter(u => !!u && u.trim().length > 0);

    this.price = formatAttractionPrice(product.starting_price, product.currency);
    const before = product.price_before_discount;
    const from = product.starting_price;
    // Only a genuine discount gets a struck-through "was" price.
    this.beforePrice = (before != null && from != null && before > from)
      ? formatAttractionPrice(before, product.currency)
      : null;

    this.durationText = this.computeDuration(product);

    const languages = (product.language_guides ?? [])
      .map(g => (g.language ? g.language.toUpperCase() : null))
      .filter((l): l is string => !!l);
    const distinctLanguages = languages.filter((l, i) => languages.indexOf(l) === i);
    this.languagesText = distinctLanguages.length ? distinctLanguages.join(', ') : null;

    this.pickupText = product.traveler_pickup
      ? this.translate.instant(
          product.traveler_pickup === PICKUP_MEET_AT_START
            ? 'attractions.pickupMeet'
            : 'attractions.pickupIncluded'
        )
      : null;

    const info = (product.additional_info ?? [])
      .map(i => i.description)
      .filter((d): d is string => !!d && d.trim().length > 0);
    this.goodToKnowText = info.length ? info.join('\n') : null;

    this.inclusions = (product.inclusions ?? [])
      .map(i => i.description)
      .filter((d): d is string => !!d && d.trim().length > 0);
    this.exclusions = (product.exclusions ?? [])
      .map(i => i.description)
      .filter((d): d is string => !!d && d.trim().length > 0);

    const cancellation = product.cancellation_policy?.description;
    this.cancellationText = cancellation && cancellation.trim().length > 0 ? cancellation : null;

    const meetingPoint = product.meeting_point?.description;
    this.meetingPointText = meetingPoint && meetingPoint.trim().length > 0 ? meetingPoint : null;

    const hasRating = product.rating != null && (product.review_count ?? 0) > 0;
    const anySource = (product.review_sources ?? []).some(s => (s.total_count ?? 0) > 0);
    this.showRatingsBreakdown = hasRating || anySource;
  }

  private computeDuration(product: Attraction): string | null {
    const minutes = product.duration_minutes;
    if (minutes == null || minutes <= 0) return null;
    if (minutes >= 60) {
      const text = minutes % 60 === 0 ? String(minutes / 60) : (minutes / 60).toFixed(1);
      return this.translate.instant('attractions.durationHours', { value: text });
    }
    return this.translate.instant('attractions.durationMinutes', { count: minutes });
  }

  hasFlag(flag: string): boolean {
    return (this.detail?.flags ?? []).indexOf(flag) !== -1;
  }

  onClose(): void {
    this.sheet.close();
  }

  retry(): void {
    if (this._code) {
      this.loadDetails(this._code);
    }
  }

  onBook(): void {
    const url = this.detail?.booking_url;
    if (url && url.trim().length > 0) {
      // Logged BEFORE window.open: this is the commission-bearing event, and switching tabs can cut
      // short anything queued after the handoff. The URL itself is deliberately never sent —
      // affiliate links run past GA4's 100-character parameter limit and would arrive truncated.
      this.analytics.log('attraction_book_click', {
        content_id: this.detail?.provider_product_id ?? this._code,
        entry_point: 'details'
      });
      // As-given, never rebuilt: this URL carries the affiliate tracking that is the revenue path.
      window.open(url, '_blank');
    }
  }
}
